import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  StyleSheet,
  View,
  Pressable,
  Animated,
  SafeAreaView,
  ScrollView,
  ActivityIndicator,
} from "react-native";

import {
  Simulate as SimulateIcon,
  Patients as PatientsIcon,
  // LeadsIcon,
  Caret,
  Star,
  Analytics,
  BeforeAfter,
  Phone,
  Message2,
  Calendar,
  Gallery,
  ClipboardEdit as ConsultsIcon,
  Build,
  Image as ImageIcon,
} from "../../../assets/index";
import { NormalText } from "../../common/native";
import { DashboardHeader } from "../../uncommon/native/index";
import { ModalOpener } from "../../uncommon/native/modals/index";
import {
  markClinicEmailVerificationNudgeDismissed,
  shouldShowClinicEmailVerificationNudge,
} from "../../../utils/clinicEmailVerification";
import useHomeLayoutController, {
  DESKTOPTABSIZESMALL,
  TABSIZE,
} from "./useHomeLayoutController";

const CLINIC_EMAIL_VERIFICATION_NUDGE_DELAY_MS = 90 * 1000;
const CLINIC_EMAIL_VERIFICATION_NUDGE_ROUTE_CHANGE_THRESHOLD = 2;

const HomeLayout: React.FC = () => {
  const routerLocation = useLocation();
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [
    showClinicEmailVerificationNudge,
    setShowClinicEmailVerificationNudge,
  ] = React.useState(false);
  const [
    clinicEmailVerificationDelayElapsed,
    setClinicEmailVerificationDelayElapsed,
  ] = React.useState(false);
  const [
    clinicEmailVerificationRouteChangeCount,
    setClinicEmailVerificationRouteChangeCount,
  ] = React.useState(0);
  const previousNudgePathRef = React.useRef(routerLocation.pathname);
  const {
    width,
    height,
    isMobile,
    session,
    isSessionLoading,
    isModalOpen,
    isMobileMenuOpen,
    isMinimized,
    animatedHeight,
    animatedTabSize,
    spinValue,
    tabs,
    handleToggleHamburger,
    handleSignOut,
    toggleDesktopSidebar,
    closeClinicInfoModal,
  } = useHomeLayoutController();

  const isTabPathActive = React.useCallback(
    (tabPath: string) => {
      const currentPath = routerLocation.pathname.toLowerCase();
      const normalizedTabPath = tabPath.toLowerCase();

      return (
        currentPath === normalizedTabPath ||
        currentPath.startsWith(`${normalizedTabPath}/`)
      );
    },
    [routerLocation.pathname],
  );

  const getTabDisplayLabel = React.useCallback((tab: string) => {
    if (tab === "Patients") return "Leads & Patients";
    if (tab === "Deploy Lead Gen") return "Deploy";
    return tab;
  }, []);

  const isClinicEmailVerificationNudgeEligible =
    shouldShowClinicEmailVerificationNudge(session);
  const hasClinicEmailVerificationNudgeDelayPassed =
    clinicEmailVerificationDelayElapsed ||
    clinicEmailVerificationRouteChangeCount >=
      CLINIC_EMAIL_VERIFICATION_NUDGE_ROUTE_CHANGE_THRESHOLD;

  React.useEffect(() => {
    setClinicEmailVerificationDelayElapsed(false);
    setClinicEmailVerificationRouteChangeCount(0);

    if (!isClinicEmailVerificationNudgeEligible) return;

    const timeoutId = window.setTimeout(() => {
      setClinicEmailVerificationDelayElapsed(true);
    }, CLINIC_EMAIL_VERIFICATION_NUDGE_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isClinicEmailVerificationNudgeEligible, session?.userId]);

  React.useEffect(() => {
    if (!isClinicEmailVerificationNudgeEligible) {
      previousNudgePathRef.current = routerLocation.pathname;
      return;
    }

    if (previousNudgePathRef.current === routerLocation.pathname) return;

    previousNudgePathRef.current = routerLocation.pathname;
    setClinicEmailVerificationRouteChangeCount((currentCount) =>
      Math.min(
        currentCount + 1,
        CLINIC_EMAIL_VERIFICATION_NUDGE_ROUTE_CHANGE_THRESHOLD,
      ),
    );
  }, [isClinicEmailVerificationNudgeEligible, routerLocation.pathname]);

  React.useEffect(() => {
    if (
      isModalOpen ||
      !isClinicEmailVerificationNudgeEligible ||
      !hasClinicEmailVerificationNudgeDelayPassed
    ) {
      setShowClinicEmailVerificationNudge(false);
      return;
    }

    setShowClinicEmailVerificationNudge(true);
  }, [
    hasClinicEmailVerificationNudgeDelayPassed,
    isClinicEmailVerificationNudgeEligible,
    isModalOpen,
  ]);

  const closeClinicEmailVerificationNudge = React.useCallback(() => {
    markClinicEmailVerificationNudgeDismissed(session?.userId);
    setShowClinicEmailVerificationNudge(false);
  }, [session?.userId]);

  const renderTabs = (isShowIcon: boolean) => {
    return tabs.map((tab) => {
      const tabPath =
        tab === "Gallery"
          ? "/photo-gallery"
          : tab === "Deploy Lead Gen"
            ? "/deploy-widget"
            : `/${tab.toLowerCase().replace(" ", "-")}`;
      const shouldHighlightUpgrade =
        tab === "Upgrade" && session?.planType === "FREE";

      return (
        <React.Fragment key={tab}>
          {/* Only show section separators when not minimized */}
          {!isMinimized && isShowIcon && tab === "Simulate" && (
            <View style={styles.sectionSeparatorContainer}>
              <NormalText
                text={"Photos"}
                style={styles.textSeperator}
                numberOfLines={1}
              />
            </View>
          )}
          {!isMinimized && isShowIcon && tab === "Patients" && (
            <View style={styles.sectionSeparatorContainer}>
              <NormalText
                text={"Leads & Patients"}
                style={styles.textSeperator}
                numberOfLines={1}
              />
            </View>
          )}
          {!isMinimized && isShowIcon && tab === "Key Management" && (
            <View style={styles.sectionSeparatorContainer}>
              <NormalText
                text={"Admin Tools"}
                style={styles.textSeperator}
                numberOfLines={1}
              />
            </View>
          )}
          <NavLink
            key={tab}
            onClick={() => {
              if (isTabPathActive(tabPath)) {
                setRefreshNonce((currentNonce) => currentNonce + 1);
              }
              handleToggleHamburger();
            }}
            to={tabPath}
            style={({ isActive }) => ({
              ...{
                textDecoration: "none",
                background: isActive
                  ? "rgba(0, 183, 244, 0.1)"
                  : shouldHighlightUpgrade
                    ? "#A1E3CB"
                    : "transparent",
                marginTop:
                  tab === "Upgrade" && isShowIcon && !shouldHighlightUpgrade
                    ? "auto"
                    : "0",
              },
            })}
          >
            <View
              style={[
                styles.tabButton,
                // Center the icons when minimized
                isMinimized && isShowIcon && { justifyContent: "center" },
              ]}
            >
              {isShowIcon && tab === "Messages" && <Message2 />}
              {isShowIcon && tab === "Conversations" && <Phone />}
              {isShowIcon && tab === "Patients" && <PatientsIcon />}
              {isShowIcon && tab === "Appointments" && <Calendar />}
              {isShowIcon && tab === "Before-Afters" && <BeforeAfter />}
              {isShowIcon && tab === "Gallery" && <Gallery />}
              {isShowIcon && tab === "Image Tools" && (
                <ImageIcon color="#000000" />
              )}
              {isShowIcon && tab === "Simulate" && <SimulateIcon />}
              {isShowIcon && tab === "Analytics" && <Analytics />}
              {isShowIcon && tab === "Leads" && <PatientsIcon />}
              {isShowIcon && tab === "Deploy Lead Gen" && <Phone />}
              {isShowIcon && tab === "Key Management" && <Build />}
              {isShowIcon && tab === "Consults" && <ConsultsIcon />}
              {isShowIcon && tab === "Upgrade" && (
                <Star color={shouldHighlightUpgrade ? "#000000" : undefined} />
              )}

              {/* Only show text when not minimized */}
              {(!isMinimized || !isShowIcon) && (
                <View style={styles.tabLabelContainer}>
                  <NormalText
                    text={getTabDisplayLabel(tab)}
                    numberOfLines={1}
                    style={
                      shouldHighlightUpgrade ? { color: "#000000" } : undefined
                    }
                  />
                </View>
              )}
            </View>
          </NavLink>
        </React.Fragment>
      );
    });
  };

  if (isSessionLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00B7F4" />
      </View>
    );
  }

  if (!session) return null;

  return (
    <SafeAreaView style={{ flex: 1, width: "100%", minWidth: 0 }}>
      <ScrollView
        style={{ flex: 1, width: "100%" }}
        contentContainerStyle={{ minWidth: 0, width: "100%", flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
        <DashboardHeader
          handleToggleHamburger={handleToggleHamburger}
          isMobileMenuOpen={isMobileMenuOpen}
        />
        {isMobile ? (
          <Animated.View
            style={[
              styles.mobileMenu,
              {
                height: animatedHeight,
              },
            ]}
          >
            {renderTabs(false)}
            <Pressable style={[styles.tabButton]} onPress={handleSignOut}>
              <NormalText text="Sign Out" />
            </Pressable>
          </Animated.View>
        ) : null}
        <View
          style={[
            styles.dashboardBody,
            {
              height: height - 55,
              display: "flex",
              justifyContent: "flex-start",
              minWidth: 0,
            },
          ]}
        >
          {!isMobile ? (
            <Animated.View
              style={[
                styles.dashboardBodyLeft,
                {
                  zIndex: 1,
                  width: animatedTabSize,
                },
              ]}
            >
              {renderTabs(true)}
              <Pressable
                style={{
                  zIndex: 999,
                  position: "absolute",
                  right: 0,
                  top: 28,
                  transform: "translate(50%, 0%)",
                  borderRadius: 50,
                  backgroundColor: "#fff",
                  shadowColor: "#000",
                  shadowOffset: {
                    width: 0,
                    height: 2,
                  },
                  shadowOpacity: 0.25,
                  shadowRadius: 3.84,
                  elevation: 5,
                }}
                onPress={toggleDesktopSidebar}
              >
                <Animated.View
                  style={{
                    transform: [
                      {
                        rotate: spinValue.interpolate({
                          inputRange: [0, 1],
                          outputRange: ["0deg", "180deg"],
                        }),
                      },
                    ],
                  }}
                >
                  <Caret />
                </Animated.View>
              </Pressable>
            </Animated.View>
          ) : null}
          <Animated.View
            key={`${routerLocation.pathname}:${refreshNonce}`}
            style={[
              {
                padding: isMobile ? 10 : 20,
                width: isMobile
                  ? undefined
                  : animatedTabSize.interpolate({
                      inputRange: [DESKTOPTABSIZESMALL, TABSIZE],
                      outputRange: [
                        width - DESKTOPTABSIZESMALL,
                        width - TABSIZE,
                      ],
                    }),
                flex: 1,
                alignSelf: "stretch",
                minHeight: 0,
                minWidth: 0,
                maxWidth: "100%",
                flexShrink: 1,
              },
            ]}
          >
            <Outlet />
          </Animated.View>
        </View>
        <ModalOpener
          visible={isModalOpen}
          onClose={closeClinicInfoModal}
          modalType={"FillOutClinicInfo"}
        />
        <ModalOpener
          visible={showClinicEmailVerificationNudge}
          onClose={closeClinicEmailVerificationNudge}
          modalType={"ClinicEmailVerificationNudge"}
        />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  dashboardBody: {
    flexDirection: "row",
    width: "100%",
  },
  dashboardBodyLeft: {
    width: "100%",
    gap: 12,
    paddingVertical: 20,
    borderRightColor: "#DDDEE0",
    borderRightWidth: 1,
  },
  tabButton: {
    flexWrap: "nowrap",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 24,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 4,
  },
  mobileMenu: {
    gap: 8,
    marginTop: 10,
    width: "100%",
    backgroundColor: "#fff",
    position: "absolute",
    top: 55,
    left: 0,
    right: 0,
    overflow: "hidden",
    zIndex: 9999,
  },
  textSeperator: {
    color: "#C4C6CD",
    fontSize: 14,
  },
  tabLabelContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  sectionSeparatorContainer: {
    borderTopColor: "#DDDEE0",
    borderTopWidth: 1,
    paddingTop: 10,
    paddingLeft: 18,
    paddingRight: 10,
  },
});

export default HomeLayout;
